import React, { useState, useEffect, useContext } from 'react';
import { StorageContext } from '../../contexts/StorageContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import PromptPanel from '../PromptPanel';
import ResponsePanel from '../ResponsePanel';
import PromptGenerator from '../../services/PromptGenerator';
import ResponseProcessor from '../../services/ResponseProcessor';
import './FinishingPhase.css';

/**
 * Finishing phase component
 * Handles final optimizations and finalizing implementation
 */
function FinishingPhase() {
  const { storage } = useContext(StorageContext);
  const { project, updateProject } = useContext(ProjectContext);
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [promptGenerator] = useState(new PromptGenerator());
  const [responseProcessor] = useState(new ResponseProcessor(storage));

  // Load files and stats
  useEffect(() => {
    const loadData = async () => {
      if (!project) return;
      
      try {
        const projectFiles = await storage.getFilesByProject(project.id);
        setFiles(projectFiles);
        
        // Calculate project stats
        const components = await storage.getComponentsByProject(project.id);
        const batches = await storage.getBatchesByProject(project.id);
        
        const totalLOC = projectFiles.reduce((sum, file) => sum + (file.actualLoc || file.estimatedLoc || 0), 0);
        const completedBatches = batches.filter(b => b.status === 'completed').length;
        
        setStats({
          totalFiles: projectFiles.length,
          totalComponents: components.length,
          totalBatches: batches.length,
          completedBatches,
          totalLOC
        });
      } catch (error) {
        console.error('Error loading project data:', error);
      }
    };
    
    loadData();
  }, [project, storage]);

  // Generate finishing prompt
  const handleGeneratePrompt = async () => {
    if (files.length === 0) {
      alert('No files found. Please complete the previous phases first.');
      return;
    }
    
    try {
      setIsGeneratingPrompt(true);
      const generatedPrompt = await promptGenerator.generateFinishingPrompt(project, files);
      setPrompt(generatedPrompt);
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert('Failed to generate prompt');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Process AI response
  const handleProcessResponse = async (response) => {
    try {
      setIsProcessingResponse(true);
      
      const result = await responseProcessor.processResponse(
        response,
        'FINISHING',
        project.id
      );
      
      if (result.success) {
        // Update project with phase completion
        const updatedProject = {
          ...project,
          finishingComplete: true,
          completed: true,
          updated: Date.now()
        };
        
        await storage.updateProject(updatedProject);
        updateProject(updatedProject);
        
        // Store processing results
        setProcessingResults(result);
        
        return { success: true };
      } else {
        return { 
          success: false,
          error: result.error || 'Failed to process response'
        };
      }
    } catch (error) {
      console.error('Error processing response:', error);
      return { 
        success: false,
        error: 'Error processing response: ' + error.message 
      };
    } finally {
      setIsProcessingResponse(false);
    }
  };

  // Generate project summary
  const renderProjectSummary = () => {
    if (!stats) {
      return (
        <div className="loading-stats">Loading project statistics...</div>
      );
    }

    return (
      <div className="project-stats">
        <div className="stat-item">
          <div className="stat-value">{stats.totalFiles}</div>
          <div className="stat-label">Files</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{stats.totalComponents}</div>
          <div className="stat-label">Components</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{stats.completedBatches}/{stats.totalBatches}</div>
          <div className="stat-label">Batches</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{stats.totalLOC.toLocaleString()}</div>
          <div className="stat-label">Lines of Code</div>
        </div>
      </div>
    );
  };

  // Render file type summary
  const renderFileTypeSummary = () => {
    if (files.length === 0) {
      return (
        <div className="no-files-message">No files found.</div>
      );
    }

    // Group files by language/type
    const fileTypes = {};
    files.forEach(file => {
      const language = file.language || 'unknown';
      if (!fileTypes[language]) {
        fileTypes[language] = {
          count: 0,
          loc: 0
        };
      }
      fileTypes[language].count++;
      fileTypes[language].loc += file.actualLoc || file.estimatedLoc || 0;
    });

    return (
      <div className="file-type-summary">
        <h4>Files by Type</h4>
        <div className="file-type-list">
          {Object.entries(fileTypes).map(([type, data]) => (
            <div key={type} className="file-type-item">
              <div className="file-type-name">{type}</div>
              <div className="file-type-stats">
                <span className="file-type-count">{data.count} files</span>
                <span className="file-type-loc">{data.loc.toLocaleString()} lines</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="finishing-phase">
      <div className="phase-description">
        <h2>Finishing Phase</h2>
        <p>
          In this final phase, you'll verify the implementation, apply performance optimizations,
          and finalize the project for delivery.
        </p>
      </div>
      
      <div className="project-summary">
        <h3>Project Summary</h3>
        {renderProjectSummary()}
        {renderFileTypeSummary()}
      </div>
      
      {processingResults && (
        <div className="optimization-results">
          <h3>Optimization Results</h3>
          <div className="results-summary">
            <p>Applied {processingResults.optimizations} optimizations to improve performance and quality.</p>
            {processingResults.details && (
              <ul className="optimization-list">
                {processingResults.details.map((opt, index) => (
                  <li key={index} className="optimization-item">
                    <span className="optimization-path">{opt.path}</span>
                    <span className="optimization-type">{opt.type}</span>
                    <span className="optimization-description">{opt.description}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      
      <div className="ai-interaction">
        <PromptPanel 
          prompt={prompt}
          onGeneratePrompt={handleGeneratePrompt}
          isLoading={isGeneratingPrompt}
          title="Finishing Prompt"
        />
        
        <ResponsePanel 
          onProcessResponse={handleProcessResponse}
          isProcessing={isProcessingResponse}
          processingResults={processingResults}
          title="Finishing Response"
        />
      </div>
      
      <div className="completion-section">
        <h3>Project Completion</h3>
        <p className="completion-message">
          Congratulations! You've completed all phases of your project implementation.
          You can now export your project or continue refining it as needed.
        </p>
        <div className="completion-actions">
          <button className="export-btn">Export Project</button>
          <button className="share-btn">Share Project</button>
        </div>
      </div>
    </div>
  );
}

export default FinishingPhase;
